/**
 * websocket-connection.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { ReplaySubject, Subject } from "rxjs/Rx";
import { ConnectionFailure, ConnectionFailureMessage, Message }
  from "../messages/general/";
import { Connection } from "./connection";
import { CloseCode, ReadyState } from "./enums";

const replayLimit : number = 4;

// Debug setting controlling the log for all messages.
const verboseSocket : boolean = false;

const EXPECTED_DISCONNECT_TIME : number = 10 * 1000;

export class WebSocketConnection extends Connection<Message> {

  private socket : WebSocket;

  public incoming : Subject<Message> = new Subject<Message>();

  private outgoing : Subject<Message> = new ReplaySubject<Message>(replayLimit);

  private _dead : boolean = false;
  public get dead () : boolean
  {
    return this._dead;
  }
  public set dead (value : boolean)
  {
    this._dead = true;
  }
  private assertAlive (action : string) : void
  {
    if (this.dead)
    {
      throw new Error("WebSocket is dead, but received command to " + action);
    }
  }

  private id : string;

  private opened : boolean = false;

  constructor (
    public url : string,
    public disconnectMessage : Message,
    id : number = 0
  ) {
    super();
    this.id = "[ws" + id + "]";

    // Create the socket and attach its hooks
    this.socket = new WebSocket(url);
    this.socket.onmessage = (event : MessageEvent) => this.receive(event);
    this.socket.onerror = (error : Event) => this.receiveError(error);
    this.socket.onclose = (close : CloseEvent) => this.receiveClose(close);
    this.socket.onopen = (openEvent : Event) => this.receiveOpen(openEvent);
  }

  private receiveError (error : Event) : void
  {
    if (this.opened)
    {
      // There really isn't any safe or reliable reaction to WebSocket errors.
      // Closure-causing errors will also generate a CloseEvent which will
      // actually have details (this error Event tends to have... none), so
      // close actions are better handled elsewhere. This is just logging.
      console.error(this.id + " encountered an error:", error);
    }
    else
    {
      // If the initial connection fails, we won't get a close (because it was
      // never open), so we need to catch this and handle it appropriately.
      this.incoming.error(
        new ConnectionFailureMessage(ConnectionFailure.NoResponse));
      this.incoming.complete();
    }
  }

  private receiveClose (close : CloseEvent) : void
  {
    if (verboseSocket)
    {
      console.info(this.id + " closing:", close);
    }
    this.dead = true;
    this.incoming.complete();
  }

  private receiveOpen (open : Event) : void
  {
    this.opened = true;
    if (verboseSocket)
    {
      console.info(this.id + " open:", open);
    }
    this.outgoing.subscribe(
      (message : Message) => this.internalSend(message),
      (err) => this.outgoingError(err),
      () => this.outgoingClose()
    );
  }

  private receive (event : MessageEvent) : void
  {
    if (verboseSocket)
    {
      console.groupCollapsed(this.id + " incoming <=|");
      console.log(event.data);
      console.groupEnd();
    }

    let message : Message;
    try
    {
      message = JSON.parse(event.data);
    }
    catch (err)
    {
      return this.incomingParseError(err, event.data);
    }
    this.incoming.next(message);
  }

  private incomingParseError (err : any, data : string) : void
  {
    if (err instanceof SyntaxError)
    {
      console.error(this.id + " received malformed JSON from the server:", err);
      // TODO: send log to server
      // TODO: disconnect?
    }
    else
    {
      console.error(
        this.id + " hit an unknown error trying to read server data:", err);
      // TODO: send log to server
      // TODO: disconnect?
    }
  }

  private internalSend (message : Message) : void
  {
    if (verboseSocket)
    {
      console.groupCollapsed(this.id + " outgoing |=>");
      console.log(message);
      console.groupEnd();
    }

    // NOTE: If you try to send data that cannot be stringified (i.e. circular)
    // it's your own damn fault. I'm not gonna catch it, because what then?
    this.socket.send(JSON.stringify(message));
  }

  public send (message : Message) : void
  {
    this.assertAlive("send a message");
    this.outgoing.next(message);
  }

  public sendRaw (message : string | ArrayBuffer | Blob) : void
  {
    // TODO: This needs to live somewhere else once we actually need it.
    // (A different connection class? Binary uploads will be sidechanneled.)
    console.warn("connection.sendRaw is for debugging only!");
    this.socket.send(message);
    // NOTE: Again obviously this is bad code in any non-testing situation,
    // because we're skipping entirely over the outgoing stream and accessing
    // the socket directly.
  }

  public close () : void
  {
    if (verboseSocket)
    {
      console.log(this.id + " was instructed to close()");
    }
    this.assertAlive("close the stream");
    this.outgoing.complete();
  }

  private outgoingError (err : any) : void
  {
    // We do not use error as an (outgoing) signal (anymore) so this should
    // never happen. Handle aggressively and willing to abort.
    throw new Error(this.id + "'s outgoing stream stopped due to error");
  }

  private outgoingClose () : void
  {
    this.internalSend(this.disconnectMessage);
    setTimeout(() => this.verifySocketClosed(), EXPECTED_DISCONNECT_TIME);
  }

  private verifySocketClosed () : void
  {
    if (this.socket.readyState !== ReadyState.Closed)
    {
      console.error(
        this.id + " sent disconnect, but socket is still open after "
          + EXPECTED_DISCONNECT_TIME + "ms; closing manually..."
      );
      // TODO: submit error to server logs?
      // We still want the connection to go away, so close it manually.
      this.socket.close(CloseCode.Normal);
    }
  }

}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/connection/websocket-connection.ts